import { useState } from 'react'
import { Navigate, useLocation } from 'react-router'
import { Button } from '@/components/ui/button'
import { getErrorMessage } from '@/api/errors'
import { useAuth } from './AuthProvider'

export function LoginPage() {
  const { status, signIn } = useAuth()
  const location = useLocation()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const stateReturnTo = (location.state as { returnTo?: unknown } | null)?.returnTo
  const queryReturnTo = new URLSearchParams(location.search).get('returnTo')
  const candidateReturnTo = typeof stateReturnTo === 'string' ? stateReturnTo : queryReturnTo
  // Only permit an internal relative app path. Never turn this into an open redirect.
  const returnTo = candidateReturnTo?.startsWith('/') && !candidateReturnTo.startsWith('//')
    ? candidateReturnTo
    : '/'

  if (status === 'signed-in' || status === 'external') return <Navigate to={returnTo} replace />

  async function handleSignIn() {
    setBusy(true)
    setError(null)
    try {
      await signIn()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-background p-6">
      {/* The logo's blue-to-violet, thrown softly behind the card. It is the only decoration
          on the page, and the first thing anyone sees of the product. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 size-[36rem] -translate-x-1/2 rounded-full bg-gradient-to-br from-brand to-brand-accent opacity-10 blur-3xl"
      />
      <div className="relative w-full max-w-sm overflow-hidden rounded-xl border border-border bg-card shadow-e4">
        <div aria-hidden className="h-1 w-full bg-gradient-to-r from-brand to-brand-accent" />
        <div className="p-8">
          <img src="/brand/tapwork-wordmark.png" alt="TapWork" className="h-14 w-auto" />
          <h1 className="sr-only">TapWork</h1>
          <p className="mt-5 text-sm text-muted-foreground">
            Track, manage and deliver the company’s work in one place.
          </p>

          <Button className="mt-6 w-full" size="lg" onClick={handleSignIn} disabled={busy}>
            {busy ? 'Opening Google…' : 'Continue with Google'}
          </Button>

          {error ? (
            <p
              role="alert"
              className="mt-3 rounded-md border border-danger/25 bg-danger-soft px-3 py-2 text-xs text-danger"
            >
              {error}
            </p>
          ) : null}

          <p className="mt-5 flex items-center gap-1.5 border-t border-border-soft pt-4 text-tiny text-text-muted">
            Clients: sign in with your work email and we will take you to your portal.
          </p>
        </div>
      </div>
    </div>
  )
}
